import FloatingInput from "../UI/FloatingInput";
import FloatingTextarea from "../UI/FloatingTextarea";
import ToggleSwitch from "../UI/ToggleSwitch";
import SelectBox from "../UI/SelectBox";
import React, { useState, useEffect, useRef } from "react";
import { getAssetUrl } from "../../services/api";
import "./ConfigField.css";

export default function ConfigField({
  category,
  fieldKey,
  value,
  type,
  label,
  onChange,
  options = [],
  meta = {},
  isAdmin = false,
  onMetaChange,
  // Optional extra control (e.g. an EntityTranslationsButton) rendered
  // right beside this field's own input instead of on a separate row —
  // see ConfigSection's own sideActions prop, which is what actually
  // supplies this per-field. Passed through by ConfigPage.jsx for
  // general.system_name/general.system_description today.
  sideAction = null,
}) {
  const [preview, setPreview] = useState(null);
  const [metaState, setMetaState] = useState(meta);
  const dropRef = useRef();

  useEffect(() => {
    if (type === "file") {
      if (value instanceof File) {
        const url = URL.createObjectURL(value);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
      } else if (typeof value === "string" && value) {
        setPreview(getAssetUrl(value));
      } else {
        setPreview(null);
      }
    }
  }, [value, type]);

  const handleChange = (cat, key, val) => {
    onChange(cat, key, val);
  };

  const handleMetaChange = (key, checked) => {
    const updatedMeta = { ...metaState, [key]: checked };
    setMetaState(updatedMeta);
    onMetaChange && onMetaChange(category, fieldKey, updatedMeta);
  };

  // ✅ العناصر حسب النوع
  let input;
  switch (type) {
    case "checkbox":
      input = (
        <ToggleSwitch
          label={label}
          checked={!!value}
          name={fieldKey}
          onChange={(val) => handleChange(category, fieldKey, val)}
        />
      );
      break;

    case "number":
    case "text":
    case "password":
      input = (
        <FloatingInput
          label={label}
          placeholder=" "
          name={fieldKey}
          type={type}
          value={value || ""}
          onChange={(e) => handleChange(category, fieldKey, e.target.value)}
          error={""}
          icon={""}
        />
        
      );
      break;

    case "textarea":
      input = (
        <FloatingTextarea
          label={label}
          name={fieldKey}
          value={value || ""}
          onChange={(e) => handleChange(category, fieldKey, e.target.value)}
          rows={3}
        />
      );
      break;

    case "select":
      input = (
        <SelectBox
          label={label}
          name={fieldKey}
          value={value}
          options={(options || []).map((opt) => ({ value: opt, label: opt }))}
          onChange={(val) => handleChange(category, fieldKey, val)}
        />
      );
      break;

    case "file":
      // hasValue يغطي الحالتين: ملف تم اختياره لتوه (File لسه ما اترفعش) أو
      // مسار محفوظ بالفعل جاي من السيرفر (string) — في الحالتين لازم زرار
      // الحذف يظهر، وفي الحالتين مفيش قيمة (لا File ولا نص) يفضل مخفي.
      const hasValue = value instanceof File || (typeof value === "string" && value);
      input = (
        <div className="mb-1">
          <label className="block mb-2 text-sm font-medium text-gray-700">{label}</label>
          <div
            ref={dropRef}
            className="file-drop-area border border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              file && handleChange(category, fieldKey, file);
            }}
          >
            <p className="text-gray-600 text-sm mb-2">
              Drag & Drop image here or click to select
            </p>
            <input type="file" onChange={(e) => handleChange(category, fieldKey, e.target.files[0])} />
            {preview && <img alt="Preview" src={preview} className="file-preview mt-3 max-h-24 mx-auto" />}
          </div>
          {hasValue && (
            // مش جوه file-drop-area عمدًا: الـ input[type=file] هناك بيغطي
            // المساحة كلها بـ opacity:0 (شوف ConfigField.css)، فلو الزرار ده
            // كان جواها كان ضغطه هيفتح نافذة اختيار ملف بدل ما يمسح القيمة.
            <button
              type="button"
              className="mt-2 text-sm text-red-500 hover:text-red-600 hover:underline"
              onClick={(e) => {
                e.preventDefault();
                handleChange(category, fieldKey, "");
              }}
            >
              ✕ Remove {label}
            </button>
          )}
        </div>
      );
      break;

    case "color":
      input = (
        <div className="mt-4">
          <label className="block mb-1 text-sm font-medium text-gray-700">{label}</label>
          <input
            type="color"
            value={value || "#000000"}
            onChange={(e) => handleChange(category, fieldKey, e.target.value)}
            className="w-12 h-8 border rounded cursor-pointer"
          />
        </div>
      );
      break;

      case "json_map":
      // التأكد أن القيمة عبارة عن كائن
      const mapValues = typeof value === "object" && value !== null ? value : {};

      input = (
        <div className="json-map-container border p-3 rounded bg-white">
          <label className="block mb-2 text-sm font-bold text-gray-700">{label}</label>
          
          {/* عرض الروتس الحالية */}
          {Object.entries(mapValues).map(([route, limit], index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <span className="flex-1 text-sm bg-gray-100 p-2 rounded">{route}</span>
              <input
                type="number"
                value={limit}
                onChange={(e) => {
                  const newMap = { ...mapValues, [route]: Number(e.target.value) };
                  handleChange(category, fieldKey, newMap);
                }}
                className="w-20 p-2 border rounded text-center"
              />
              <button
                type="button"
                className="text-red-500 px-2"
                onClick={() => {
                  const newMap = { ...mapValues };
                  delete newMap[route];
                  handleChange(category, fieldKey, newMap);
                }}
              >
                ✕
              </button>
            </div>
          ))}

          {/* إضافة روت جديد */}
          <div className="flex gap-2 mt-3 pt-3 border-t">
            <input
              type="text"
              id={`new-route-${fieldKey}`}
              placeholder="route name (e.g. users)"
              className="flex-1 p-2 text-sm border rounded"
            />
            <button
              type="button"
              className="bg-green-500 text-white px-3 py-1 rounded text-sm"
              onClick={() => {
                const input = document.getElementById(`new-route-${fieldKey}`);
                const routeName = input.value.trim();
                if (routeName && !mapValues[routeName]) {
                  const newMap = { ...mapValues, [routeName]: 10 }; // ليميت افتراضي 10
                  handleChange(category, fieldKey, newMap);
                  input.value = ""; // مسح الانبوت
                }
              }}
            >
              + Add Route
            </button>
          </div>
        </div>
      );
      break;

    default:
      input = (
        <FloatingInput
          label={label}
          name={fieldKey}
          value={value || ""}
          onChange={(e) => handleChange(category, fieldKey, e.target.value)}
        />
      );
  }

  return (
    <div className="mb-6">
      {sideAction ? (
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">{input}</div>
          {sideAction}
        </div>
      ) : (
        input
      )}

      {isAdmin && (
        <div className="flex gap-6 mt-2 text-sm text-gray-600">
          {["public", "system", "secure"].map((flag) => (
            <label key={flag} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={metaState?.[flag] || false}
                onChange={(e) => handleMetaChange(flag, e.target.checked)}
              />
              {flag.charAt(0).toUpperCase() + flag.slice(1)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
